import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Award, Zap } from 'lucide-react';
import { RevealOnScroll, FadeSlide } from './AppleScrollEffects';

export const AboutSectionHTAG = () => {
  const values = [
    {
      icon: MapPin,
      title: 'Proximité',
      description: 'Basé à Château-Porcien, intervient partout en visio ou sur site.',
    },
    {
      icon: Zap,
      title: 'Efficacité',
      description: 'Solutions rapides, pragmatiques et mesurables pour vos défis.',
    },
    {
      icon: Award,
      title: 'Expertise',
      description: 'Transformation digitale, développement web et conseil stratégique.',
    },
  ];

  return (
    <section className="apple-section relative">
      {/* Background subtle gradient */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
            filter: 'blur(80px)'
          }}
        />
      </div>

      <div className="container mx-auto relative z-10">
        <RevealOnScroll className="text-center mb-20">
          <h2 className="apple-title text-5xl lg:text-6xl mb-6">
            À propos d'
            <span className="apple-gradient-text">HTAG Facility</span>
          </h2>
        </RevealOnScroll>

        <div className="grid lg:grid-cols-2 gap-16 mb-24 max-w-6xl mx-auto">
          {/* Main text */}
          <FadeSlide direction="left" className="space-y-6">
            <p className="text-lg leading-relaxed" style={{ color: '#f5f5f7' }}>
              <strong style={{ fontWeight: 600 }}>Eric Herduin</strong> est un expert en transformation 
              digitale et développement web passionné par l'accompagnement des TPE et PME.
            </p>

            <p className="apple-subtitle leading-relaxed">
              Avec près de deux décennies d'expérience, Eric aide les entreprises à moderniser 
              leur infrastructure informatique, créer leur présence web et structurer leurs équipes.
            </p>

            <p className="apple-subtitle leading-relaxed">
              Son approche privilégie la clarté, l'écoute et l'efficacité. Il croit que la 
              technologie doit servir votre métier, pas l'inverse. Qu'il s'agisse de conseil 
              stratégique, de développement sur mesure ou de coaching d'équipe, Eric apporte 
              la rigueur et la pédagogie nécessaires pour que vos projets réussissent.
            </p>

            <p className="apple-subtitle leading-relaxed">
              <strong style={{ color: '#0071e3', fontWeight: 500 }}>Basé à Château-Porcien</strong>, 
              HTAG Facility intervient partout en France via visioconférence ou en direct. 
              Proximité et efficacité sont les piliers de chaque mission.
            </p>
          </FadeSlide>

          {/* Avatar card */}
          <FadeSlide direction="right">
            <div className="flex items-center justify-center h-full">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="apple-card w-80 h-80 flex items-center justify-center p-8"
              >
                <div className="text-center">
                  <motion.div 
                    className="text-7xl font-bold mb-6"
                    style={{ 
                      background: 'linear-gradient(135deg, #0071e3 0%, #00c3e3 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                    whileHover={{ rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    #
                  </motion.div>
                  <h3 className="text-2xl font-semibold mb-2" style={{ 
                    color: '#f5f5f7',
                    letterSpacing: '-0.02em'
                  }}>
                    Eric Herduin
                  </h3>
                  <p className="text-sm" style={{ color: '#86868b' }}>
                    Expert Transformation Digitale
                  </p>
                </div>
              </motion.div>
            </div>
          </FadeSlide>
        </div>

        {/* Values grid */}
        <RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                  whileHover={{ y: -8 }}
                  className="apple-card p-8 text-center group"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl apple-gradient-blue flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3" style={{ 
                    color: '#f5f5f7',
                    letterSpacing: '-0.02em'
                  }}>
                    {value.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#86868b' }}>
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </RevealOnScroll>
      </div>
      
      {/* Apple divider */}
      <div className="apple-divider mt-24 max-w-6xl mx-auto" />
    </section>
  );
};

export default AboutSectionHTAG;
